import { useState, useCallback } from 'react';
import Button from '../../components/common/Button';
import { useToast } from '../../context/ToastContext';
import './WhitelabelWizard.css';

const INITIAL_DATA = {
  logo: null,
  logoPreview: null,
  primaryColor: '#6366f1',
  secondaryColor: '#8b5cf6',
  domain: '',
  domainVerified: false,
  currency: 'USD',
  billing: 'monthly',
  payment: null,
  stripeConnected: false,
  fromName: '',
  fromEmail: '',
  supportEmail: '',
  apiKeyVisible: false,
  webhook: '',
};

const STEP_ICONS = ['🎨', '🌐', '💰', '💳', '📧', '🔗', '🚀'];
const STEP_LABELS = ['Brand', 'Domain', 'Pricing', 'Payment', 'Email', 'Integrate', 'Launch'];

const WhitelabelWizard = () => {
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(INITIAL_DATA);

  const updateData = useCallback((key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
  }, []);

  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= 7) {
      setCurrentStep(step);
    }
  }, []);

  const nextStep = () => {
    if (currentStep < 7) goToStep(currentStep + 1);
    else handleLaunch();
  };

  const prevStep = () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  };

  const getStepClass = (step) => {
    if (step < currentStep) return 'completed';
    if (step === currentStep) return 'active';
    return '';
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        updateData('logoPreview', ev.target?.result);
        updateData('logo', file);
      };
      reader.readAsDataURL(file);
      showToast('Logo uploaded successfully', 'success');
    }
  };

  const handleColorChange = (type, value) => {
    updateData(type === 'primary' ? 'primaryColor' : 'secondaryColor', value);
  };

  const handleVerifyDomain = () => {
    if (data.domain) {
      updateData('domainVerified', true);
      showToast('Domain verification initiated', 'success');
    }
  };

  const handleSelectPayment = (provider) => {
    updateData('payment', provider);
  };

  const handleConnectStripe = () => {
    updateData('stripeConnected', true);
    showToast('Stripe connected successfully', 'success');
  };

  const handleDisconnectStripe = () => {
    updateData('stripeConnected', false);
    updateData('payment', null);
    showToast('Stripe disconnected', 'info');
  };

  const handleSendTestEmail = () => {
    showToast('Test email sent!', 'success');
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText('wl_live_demo_key_12345678');
    showToast('API key copied to clipboard', 'success');
  };

  const handleCopyWidgetCode = () => {
    navigator.clipboard.writeText('<script src="https://yourdomain.com/widget.js" data-key="wl_live_..."></script>');
    showToast('Widget code copied to clipboard', 'success');
  };

  const handleSaveProgress = () => {
    showToast('Progress saved', 'success');
  };

  const handleClearProgress = () => {
    setData(INITIAL_DATA);
    setCurrentStep(1);
    showToast('Progress cleared', 'info');
  };

  const handleLaunch = () => {
    showToast('White label portal launched!', 'success');
  };

  const completedSteps = currentStep - 1;
  const progressPercent = Math.round((completedSteps / 7) * 100);

  return (
    <div className="wl-page">
      <div className="wl-container">
        {/* Header */}
        <div className="wl-header">
          <h2 className="wl-title">🎨 White Label Setup Wizard</h2>
          <p className="wl-subtitle">Complete all 7 steps to launch your branded portal</p>
        </div>

        {/* Progress Section */}
        <div className="wl-progress-section">
          <div className="wl-progress-header">
            <h3>Setup Progress</h3>
            <div className="wl-progress-count">
              <span className="wl-progress-number">{completedSteps}</span>/7 <span className="wl-progress-label">Steps</span>
            </div>
          </div>

          <div className="wl-progress-bar">
            <div className="wl-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>

          <div className="wl-steps-grid">
            {STEP_ICONS.map((icon, index) => (
              <div
                key={index}
                className="wl-step"
                onClick={() => goToStep(index + 1)}
              >
                <div className={`wl-step-icon ${getStepClass(index + 1)}`}>
                  {index + 1 < currentStep ? '✓' : icon}
                </div>
                <div className={`wl-step-label ${getStepClass(index + 1)}`}>
                  {STEP_LABELS[index]}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Brand Identity */}
        {currentStep === 1 && (
          <div className="wl-content">
            <h3 className="wl-content-title">Step 1: Brand Identity</h3>
            <p className="wl-content-subtitle">Upload your logo and customize your brand colors</p>

            <div className="wl-brand-grid">
              <div>
                <label className="wl-label">Primary Logo *</label>
                <div
                  className="wl-logo-area"
                  onClick={() => document.getElementById('logo-input')?.click()}
                >
                  {data.logoPreview ? (
                    <img src={data.logoPreview} alt="Logo preview" className="wl-logo-preview-img" />
                  ) : (
                    <>
                      <div className="wl-logo-icon">📷</div>
                      <p className="wl-logo-text">Click to upload (600×200 PNG)</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  id="logo-input"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
              </div>

              <div>
                <label className="wl-label">Brand Colors *</label>
                <div className="wl-color-group">
                  <label className="wl-color-label">Primary Color</label>
                  <div className="wl-color-row">
                    <input
                      type="color"
                      value={data.primaryColor}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="wl-color-picker"
                    />
                    <input
                      type="text"
                      value={data.primaryColor}
                      onChange={(e) => handleColorChange('primary', e.target.value)}
                      className="wl-color-input"
                    />
                  </div>
                </div>
                <div className="wl-color-group">
                  <label className="wl-color-label">Secondary Color</label>
                  <div className="wl-color-row">
                    <input
                      type="color"
                      value={data.secondaryColor}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="wl-color-picker"
                    />
                    <input
                      type="text"
                      value={data.secondaryColor}
                      onChange={(e) => handleColorChange('secondary', e.target.value)}
                      className="wl-color-input"
                    />
                  </div>
                </div>
                <div className="wl-preview-section">
                  <label className="wl-color-label">Preview</label>
                  <button
                    className="wl-preview-btn"
                    style={{ background: data.primaryColor }}
                  >
                    Primary Button
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Custom Domain */}
        {currentStep === 2 && (
          <div className="wl-content">
            <h3 className="wl-content-title">Step 2: Custom Domain</h3>
            <p className="wl-content-subtitle">Connect your own domain for a fully branded experience</p>

            <div className="wl-domain-section">
              <label className="wl-label">Your Domain *</label>
              <div className="wl-domain-row">
                <input
                  type="text"
                  placeholder="app.yourdomain.com"
                  value={data.domain}
                  onChange={(e) => updateData('domain', e.target.value)}
                  className="wl-input flex-1"
                />
                <button onClick={handleVerifyDomain} className="wl-verify-btn">
                  Verify
                </button>
              </div>
            </div>

            {data.domain && (
              <div className="wl-dns-info">
                <h4>DNS Configuration</h4>
                <p className="wl-dns-desc">Add this CNAME record to your DNS provider:</p>
                <div className="wl-dns-table">
                  <div className="wl-dns-header">
                    <div>Type</div><div>Name</div><div>Value</div><div>TTL</div>
                  </div>
                  <div className="wl-dns-row">
                    <div>CNAME</div>
                    <div>{data.domain.split('.')[0] || 'app'}</div>
                    <div>cname.auth1.ai</div>
                    <div>3600</div>
                  </div>
                </div>
              </div>
            )}

            {data.domainVerified && (
              <div className="wl-domain-status success">
                ✓ Domain verified successfully!
              </div>
            )}
          </div>
        )}

        {/* Step 3: Pricing Plans */}
        {currentStep === 3 && (
          <div className="wl-content">
            <h3 className="wl-content-title">Step 3: Pricing Plans</h3>
            <p className="wl-content-subtitle">Configure your customer-facing pricing tiers</p>

            <div className="wl-pricing-controls">
              <div className="wl-pricing-options">
                <div className="wl-option-group">
                  <label className="wl-option-label">Currency:</label>
                  <select
                    value={data.currency}
                    onChange={(e) => updateData('currency', e.target.value)}
                    className="wl-select"
                  >
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                  </select>
                </div>
                <div className="wl-option-group wl-billing-toggle">
                  <label className="wl-option-label">Billing:</label>
                  <div className="wl-billing-btns">
                    <button
                      className={`wl-billing-btn ${data.billing === 'monthly' ? 'active' : ''}`}
                      onClick={() => updateData('billing', 'monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      className={`wl-billing-btn ${data.billing === 'annual' ? 'active' : ''}`}
                      onClick={() => updateData('billing', 'annual')}
                    >
                      Annual
                    </button>
                  </div>
                </div>
              </div>
              <button className="wl-add-plan-btn">+ Add Plan</button>
            </div>

            <div className="wl-plans-grid">
              {['Starter', 'Pro', 'Enterprise'].map((plan, i) => (
                <div key={plan} className="wl-plan-card">
                  <h4 className="wl-plan-name">{plan}</h4>
                  <div className="wl-plan-price">
                    ${[29, 79, 199][i]}<span>/mo</span>
                  </div>
                  <ul className="wl-plan-features">
                    <li>✓ Feature {i + 1}</li>
                    <li>✓ Feature {i + 2}</li>
                    <li>✓ Feature {i + 3}</li>
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Payment Gateway */}
        {currentStep === 4 && (
          <div className="wl-content">
            <h3 className="wl-content-title">Step 4: Payment Gateway</h3>
            <p className="wl-content-subtitle">Connect your payment provider to process customer payments</p>

            <div className="wl-payment-grid">
              <button
                className={`wl-payment-card ${data.payment === 'stripe' ? 'selected' : ''}`}
                onClick={() => handleSelectPayment('stripe')}
              >
                <div className="wl-payment-icon">💳</div>
                <div className="wl-payment-name">Stripe</div>
              </button>
              <button
                className={`wl-payment-card ${data.payment === 'paypal' ? 'selected' : ''}`}
                onClick={() => handleSelectPayment('paypal')}
              >
                <div className="wl-payment-icon">🅿️</div>
                <div className="wl-payment-name">PayPal</div>
              </button>
              <button
                className={`wl-payment-card ${data.payment === 'other' ? 'selected' : ''}`}
                onClick={() => handleSelectPayment('other')}
              >
                <div className="wl-payment-icon">➕</div>
                <div className="wl-payment-name">Other</div>
              </button>
            </div>

            {data.payment === 'stripe' && !data.stripeConnected && (
              <div className="wl-stripe-connect">
                <div className="wl-stripe-icon">💳</div>
                <h4>Connect to Stripe</h4>
                <p>Link your Stripe account to start accepting payments</p>
                <button onClick={handleConnectStripe} className="wl-connect-btn">
                  Connect Stripe Account
                </button>
              </div>
            )}

            {data.stripeConnected && (
              <div className="wl-stripe-connected">
                <div className="wl-connected-info">
                  <div className="wl-connected-icon">💳</div>
                  <div>
                    <p className="wl-connected-title">Stripe Connected</p>
                    <p className="wl-connected-account">Account: acct_demo123</p>
                  </div>
                </div>
                <button onClick={handleDisconnectStripe} className="wl-disconnect-btn">
                  Disconnect
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Email Templates */}
        {currentStep === 5 && (
          <div className="wl-content">
            <h3 className="wl-content-title">Step 5: Email Templates</h3>
            <p className="wl-content-subtitle">Customize your transactional and marketing emails</p>

            <div className="wl-email-grid">
              <div className="wl-email-form">
                <div className="wl-form-group">
                  <label className="wl-label">From Name</label>
                  <input
                    type="text"
                    placeholder="Your Company Name"
                    value={data.fromName}
                    onChange={(e) => updateData('fromName', e.target.value)}
                    className="wl-input"
                  />
                </div>
                <div className="wl-form-group">
                  <label className="wl-label">From Email *</label>
                  <input
                    type="email"
                    placeholder="noreply@yourdomain.com"
                    value={data.fromEmail}
                    onChange={(e) => updateData('fromEmail', e.target.value)}
                    className="wl-input"
                  />
                </div>
                <div className="wl-form-group">
                  <label className="wl-label">Support Email</label>
                  <input
                    type="email"
                    placeholder="support@yourdomain.com"
                    value={data.supportEmail}
                    onChange={(e) => updateData('supportEmail', e.target.value)}
                    className="wl-input"
                  />
                </div>
              </div>
              <div className="wl-email-preview">
                <div className="wl-preview-icon">📧</div>
                <p className="wl-preview-title">Email Template Preview</p>
                <p className="wl-preview-desc">Preview your branded email templates</p>
                <button onClick={handleSendTestEmail} className="wl-test-email-btn">
                  Send Test Email
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Website Integration */}
        {currentStep === 6 && (
          <div className="wl-content">
            <h3 className="wl-content-title">Step 6: Website Integration</h3>
            <p className="wl-content-subtitle">Embed signup forms and widgets on your website</p>

            <div className="wl-form-group">
              <label className="wl-label">API Key *</label>
              <div className="wl-api-row">
                <input
                  type={data.apiKeyVisible ? 'text' : 'password'}
                  value="wl_live_demo_key_12345678"
                  readOnly
                  className="wl-input wl-api-input"
                />
                <button
                  onClick={() => updateData('apiKeyVisible', !data.apiKeyVisible)}
                  className="wl-api-btn"
                >
                  {data.apiKeyVisible ? 'Hide' : 'Show'}
                </button>
                <button onClick={handleCopyApiKey} className="wl-copy-btn">Copy</button>
                <button className="wl-api-btn">Regenerate</button>
              </div>
            </div>

            <div className="wl-form-group">
              <label className="wl-label">Customer Signup Widget</label>
              <div className="wl-widget-code">
                <code>&lt;script src="https://yourdomain.com/widget.js" data-key="wl_live_..."&gt;&lt;/script&gt;</code>
                <button onClick={handleCopyWidgetCode} className="wl-copy-widget-btn">
                  📋 Copy to Clipboard
                </button>
              </div>
            </div>

            <div className="wl-form-group">
              <label className="wl-label">Webhook URL (Optional)</label>
              <input
                type="url"
                placeholder="https://yoursite.com/webhook"
                value={data.webhook}
                onChange={(e) => updateData('webhook', e.target.value)}
                className="wl-input"
              />
              <p className="wl-hint">Receive real-time notifications for customer events</p>
            </div>
          </div>
        )}

        {/* Step 7: Test & Launch */}
        {currentStep === 7 && (
          <div className="wl-content">
            <div className="wl-launch-header">
              <h3 className="wl-content-title">Step 7: Test & Launch</h3>
              <p className="wl-content-subtitle">Review your configuration and launch your white label portal</p>
            </div>

            <div className="wl-summary-grid">
              <div className="wl-summary-card">
                <h4>Brand Settings</h4>
                <div className="wl-summary-item">
                  <span>Logo</span>
                  <span className={data.logo ? 'text-success' : 'text-danger'}>
                    {data.logo ? '✓ Uploaded' : '✗ Missing'}
                  </span>
                </div>
                <div className="wl-summary-item">
                  <span>Primary Color</span>
                  <span>{data.primaryColor}</span>
                </div>
                <div className="wl-summary-item">
                  <span>Domain</span>
                  <span>{data.domain || 'Not set'}</span>
                </div>
              </div>
              <div className="wl-summary-card">
                <h4>Integration Status</h4>
                <div className="wl-summary-item">
                  <span>Pricing Plans</span>
                  <span>3 configured</span>
                </div>
                <div className="wl-summary-item">
                  <span>Payment Gateway</span>
                  <span className={data.stripeConnected ? 'text-success' : 'text-warning'}>
                    {data.stripeConnected ? '✓ Connected' : '⏳ Pending'}
                  </span>
                </div>
                <div className="wl-summary-item">
                  <span>API Key</span>
                  <span className="text-success">✓ Generated</span>
                </div>
              </div>
            </div>

            <div className="wl-checklist">
              <h4>Pre-Launch Checklist</h4>
              <div className="wl-checklist-items">
                <label className="wl-checklist-item">
                  <input type="checkbox" defaultChecked={!!data.logo} /> Logo uploaded
                </label>
                <label className="wl-checklist-item">
                  <input type="checkbox" defaultChecked={!!data.domain} /> Domain configured
                </label>
                <label className="wl-checklist-item">
                  <input type="checkbox" defaultChecked={data.stripeConnected} /> Payment connected
                </label>
                <label className="wl-checklist-item">
                  <input type="checkbox" defaultChecked={!!data.fromEmail} /> Email configured
                </label>
              </div>
            </div>

            <div className={`wl-launch-area ${data.stripeConnected ? 'ready' : 'pending'}`}>
              {data.stripeConnected ? (
                <>
                  <div className="wl-launch-icon">🚀</div>
                  <h3>Ready to Launch!</h3>
                  <p>Your white label portal is configured and ready to go</p>
                  <Button variant="primary" onClick={handleLaunch}>Launch Portal</Button>
                </>
              ) : (
                <>
                  <div className="wl-launch-icon">⚠️</div>
                  <h3>Complete All Steps First</h3>
                  <p>Please complete all required steps before launching</p>
                  <Button variant="primary" onClick={() => goToStep(1)}>Return to Step 1</Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="wl-nav">
          <div className="wl-nav-row">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="wl-nav-btn wl-prev-btn"
            >
              ← Previous
            </button>
            <div className="wl-nav-actions">
              <button onClick={handleSaveProgress} className="wl-nav-btn">💾 Save</button>
              <button onClick={handleClearProgress} className="wl-nav-btn">🔄 Clear</button>
              <button onClick={nextStep} className="wl-nav-btn wl-next-btn">
                {currentStep === 7 ? '🚀 Launch' : 'Next Step →'}
              </button>
            </div>
          </div>
          <div className="wl-nav-hint">
            ⌨️ Keyboard: <span>Ctrl+Enter</span> Next | <span>Esc</span> Back | <span>Ctrl+S</span> Save
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhitelabelWizard;
